import { AllSessionDetailsInterface } from 'src/models/SessionDetails';
import { ConsolidatedFlowImpactInterface, SessionDefaultsInterface, SessionInterface } from '../models/Session';
import { SessionResponse, SessionsResponse, SessionDashboardResponse, SessionDetailsResponse, UserBookingInterestResponse, AllSessionDetailsResponse, SessionsAggregatedResponse } from '../models/types';
import { fetchWithAuth, defaultFetch } from "../utils/auth";

export const readSessionsAggregated = async (showId: string | string[] | undefined, pageSize: string | string[] | undefined, fromDate?: string, toDate?: string): Promise<SessionsAggregatedResponse> => {
    try {
        let url = `/api/backend/v1/sessions`
        url = url + `?showIdFilter=${showId}&pageSize=${pageSize}&excludeDeleted=true`
        if (fromDate) url += `&fromDate=${fromDate}`
        if (toDate) url += `&toDate=${toDate}`

        const res = await fetchWithAuth(url);

        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new SessionsAggregatedResponse({ data: { sessions: resJson?.sessions, shows: resJson?.shows, sessionDetailsDict: resJson?.sessionDetailsDict } });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new SessionsAggregatedResponse({ error: message })
    }
}

export const readSessionDefaults = async (): Promise<SessionDefaultsInterface> => {
    const res = await fetchWithAuth(`/api/backend/v1/sessions/defaults`);

    if (!res.ok) {
        throw new Error("Unable to load session defaults");
    }
    return await res.json();
}

/**
 * What switching this session to the consolidated flow would cost right now, so the form can
 * warn with real numbers before the admin commits (TTT-688 §10.1). Safe to call for any
 * session: an already-consolidated one answers `wouldSwitchFlow: false`.
 */
export const readConsolidatedFlowImpact = async (sessionId: number): Promise<ConsolidatedFlowImpactInterface> => {
    const res = await fetchWithAuth(`/api/backend/v1/sessions/${sessionId}/consolidated-flow-impact`);

    if (!res.ok) {
        throw new Error("Unable to load the consolidated flow impact");
    }
    return await res.json();
}

export const readSessions = async (showId: string | string[] | undefined, pageSize: string | string[] | undefined, fromDate?: string, toDate?: string): Promise<SessionsResponse> => {
    return readSessionsInternal(showId, pageSize, fromDate, toDate, false)
}

export const readScannerPageSessions = async (showId: string | string[] | undefined, pageSize: string | string[] | undefined, fromDate? : string, toDate? : string): Promise<SessionsResponse> => {
    return readSessionsInternal(showId, pageSize, fromDate, toDate, true)
}

export const readSessionsAudience = async (showId: string | string[] | undefined, pageSize: string | string[] | undefined, promoCode?: string, fromDate? : string, toDate? : string): Promise<SessionsResponse> => {
    return readSessionsInternal(showId, pageSize, fromDate, toDate, true, promoCode)
};

const readSessionsInternal = async (showId: string | string[] | undefined, pageSize: string | string[] | undefined, fromDate?: string, toDate?: string, isAudienceView?: boolean, promoCode?: string): Promise<SessionsResponse> => {
    try {
        let url = isAudienceView ? `/api/backend/v1/sessions/audience` : `/api/backend/v1/sessions`
        url = url + `?showIdFilter=${showId}&pageSize=${pageSize}&excludeDeleted=true`
        if (fromDate) url += `&fromDate=${fromDate}`
        if (toDate) url += `&toDate=${toDate}`
        if (promoCode) url += `&promoCode=${promoCode}`

        const res = isAudienceView ? await defaultFetch(url) : await fetchWithAuth(url);

        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new SessionsResponse({ data: resJson?.sessions });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new SessionsResponse({ error: message })
    }
};

export const readSession = async (sessionId: number, promoCode?: string): Promise<SessionResponse> => {
    try {
        let url = `/api/backend/v1/sessions/${sessionId}`
        if (promoCode) url += `?promoCode=${promoCode}`
        const res = await defaultFetch(url)
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new SessionResponse({ data: resJson });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load shows'
        }
        return new SessionResponse({ error: message })
    }
};

export const readAllSessionDetails = async (showId: string | string[] | undefined): Promise<AllSessionDetailsResponse> => {
    try {
        let url = `/api/backend/v1/sessions/details/byshowid/${showId}`
        const res = await fetchWithAuth(url);
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        const result = resJson.sessionDetailsDict as AllSessionDetailsInterface;

        return new AllSessionDetailsResponse({ data: result });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load session details'
        }
        return new AllSessionDetailsResponse({ error: message });
    }
};

export const readSessionDetails = async (sessionId: number | undefined): Promise<SessionDetailsResponse> => {
    try {
        let url = `/api/backend/v1/sessions/details/${sessionId}`
        const res = await fetchWithAuth(url);
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server not returned a valid response")
        }
        const resJson = await res.json();
        return new SessionDetailsResponse({ data: { ...resJson.sessionDetails.statusCounts, ...resJson.sessionDetails.additionalSessionCounts } });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to load session details'
        }
        return new SessionDetailsResponse({ error: message });
    }
};

export const readSessionDashboard = async (sessionId: number | undefined): Promise<SessionDashboardResponse> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/sessions/dashboard/${sessionId}`);
        if (res.status >= 400 && res.status < 600) {
            throw new Error("Server did not return a valid response")
        }
        const resJson = await res.json();
        return new SessionDashboardResponse({ data: resJson });
    } catch (error) {
        let message
        if (error instanceof Error) {
            message = error.message
        } else {
            message = 'Unable to fetch session health score'
        }
        return new SessionDashboardResponse({ error: message })
    }
};

export const createSession = async (session: SessionInterface): Promise<any> => {
    try {
        const image = session.image;

        const body = new FormData()
        for (const key of Object.keys(session) as Array<keyof SessionInterface>) {
            if (key === 'image') continue;
            if(session[key] != null && typeof session[key] != 'undefined') {
                body.append(`Session.${key}`, session[key] as any)
            }
        }

        if (image instanceof Blob) {
            body.append('Image', image)
        }

        const res = await fetchWithAuth(`/api/backend/v1/sessions`, {
            method: 'POST',
            body
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        const sessionId = await res.text();
        return {
            sessionId,
        };
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = 'Unable to create session';
        }
        return { error: message };
    }
};

export const archiveSession = async (sessionId: Number): Promise<any> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/sessions/archive/${sessionId}`, {
            method: "PUT"
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = 'Unable to archive session';
        }
        return { error: message };
    }
};

export const unarchiveSession = async (sessionId: Number): Promise<any> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/sessions/un-archive/${sessionId}`, {
            method: "PUT"
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = 'Unable to unarchive session';
        }
        return { error: message };
    }
}

export const deleteSession = async (sessionId: Number): Promise<any> => {
    try {
        const res = await fetchWithAuth(`/api/backend/v1/sessions/${sessionId}`, {
            method: 'DELETE',
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = 'Unable to create session';
        }
        return { error: message };
    }
};

export const editSession = async (session: SessionInterface): Promise<any> => {
    try {
        const image = session.image;

        const body = new FormData()
        for (const key of Object.keys(session) as Array<keyof SessionInterface>) {
            if (key === 'image') continue;
            if(session[key] != null && typeof session[key] != 'undefined') {
                body.append(`Session.${key}`, session[key] as any)
            }
        }

        if (image instanceof Blob) {
            body.append('Image', image)
        }

        const res = await fetchWithAuth(`/api/backend/v1/sessions`, {
            method: 'PUT',
            body
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        return res
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = 'Unable to edit session';
        }
        return { error: message };
    }
};

export const updateSessionSortIndexes = async (sessions: SessionInterface[]): Promise<any> => {
    try {
        const data = sessions.map((s, i) => {
            return {
                id: s.id,
                sortIndex: s.sortIndex
            }
        });

        const body: string = JSON.stringify(data);
        const res = await fetchWithAuth(`/api/backend/v1/sessions/updatesortindexes`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        return res
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = 'Unable to edit session sort indexes';
        }
        return { error: message };
    }
};

export const downloadSessionDataInCSV = async (sessionId: Number, sessionName: string): Promise<any> => {
    try {
        const fetchUrl = `/api/backend/v1/sessions/csv/${sessionId}`;
        let res = await fetchWithAuth(fetchUrl, {
            method: "GET",
            headers: {
                responseType: "blob",
            },
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        const url = URL.createObjectURL(await res.blob());
        const link = document.createElement("a");
        link.href = url;
        link.download = `${sessionName}.csv`;
        link.click();

        const responseObject = await res.json();
        return {
            response: responseObject.success,
        };
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = "Unable to download csv";
        }
        return { error: message };
    }
};

export const downloadSessionHistoricalDataInCSV = async (sessionId: Number, sessionName: string): Promise<any> => {
    try {
        const fetchUrl = `/api/backend/v1/sessions/csv/history/${sessionId}`;
        let res = await fetchWithAuth(fetchUrl, {
            method: "GET",
            headers: {
                responseType: "blob",
            },
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        const url = URL.createObjectURL(await res.blob());
        const link = document.createElement("a");
        link.href = url;
        link.download = `${sessionName} History.csv`;
        link.click();

        const responseObject = await res.json();
        return {
            response: responseObject.success,
        };
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = "Unable to download csv";
        }
        return { error: message };
    }
};



export const downloadSessionLeadBookersDataInCSV = async (sessionId: Number, sessionName: string): Promise<any> => {
    try {
        const fetchUrl = `/api/backend/v1/sessions/csv/leadbookers/${sessionId}`;
        let res = await fetchWithAuth(fetchUrl, {
            method: "GET",
            headers: {
                responseType: "blob",
            },
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        const url = URL.createObjectURL(await res.blob());
        const link = document.createElement("a");
        link.href = url;
        link.download = `${sessionName} Lead bookers.csv`;
        link.click();

        const responseObject = await res.json();
        return {
            response: responseObject.success,
        };
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = "Unable to download ticket";
        }
        return { error: message };
    }
};

export const getSessionScanStatistics = async (sessionId: number): Promise<any> => {
    try {
        const fetchUrl = `/api/backend/v1/sessions/scanstatistics/${sessionId}`;
        let res = await fetchWithAuth(fetchUrl, {
            method: "GET"
        });
        if (res.status >= 400 && res.status < 600) {
            const resText = await res.text();
            console.warn(resText);
            throw new Error("Server not returned a valid response");
        }
        const responseObject = await res.json();
        return { data: responseObject };
    } catch (error) {
        let message;
        if (error instanceof Error) {
            message = error.message;
        } else {
            message = "Unable to get session scan statistics";
        }
        return { error: message };
    }

};